/*
Copyright (C) 2020  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import * as React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { ChevronDown, Monitor, Network } from "lucide-react";

import { DeploymentItem, TransferItem, ActionItem, getTransferItemTitle } from "@src/@types/MainItem";
import { MinionMachine, MinionPool } from "@src/@types/MinionPool";
import { KeyValueGrid, SectionCard } from "@src/components/ui/DetailsKit";
import IconTile from "@src/components/ui/IconTile";
import EmptyState from "@src/components/ui/Lists/EmptyState";
import {
  Cell,
  FlexCell,
  ListHeader,
  ListRow,
  Mono,
  Muted,
  Pill,
  StatePill,
  T9,
  formatDateTime,
  formatShortDateTime,
  timeAgo,
} from "@src/components/ui/Lists/ListTable";
import type { ListColumn } from "@src/components/ui/Lists/ListTable";
import { Segmented } from "@src/components/ui/Lists/ListToolbar";
import PasswordValue from "@src/components/ui/PasswordValue";

export const MACHINE_COLUMNS: ListColumn[] = [
  { key: "machine", label: "Agent machine", width: "minmax(240px, 2fr)" },
  { key: "allocation", label: "Allocation", width: "132px" },
  { key: "power", label: "Power", width: "132px" },
  { key: "action", label: "Allocated to", width: "minmax(180px, 1.5fr)" },
  { key: "lastUsed", label: "Last used", width: "120px" },
  { key: "created", label: "Created", width: "120px" },
];

const Table = styled.div`
  border-top: 1px solid ${T9.slate100};
  border-radius: 0 0 8px 8px;
  overflow: hidden;
  overflow-x: auto;
`;
const TableInner = styled.div`
  min-width: 1040px;
`;
const Chevron = styled(ChevronDown)<{ open?: boolean }>`
  color: ${T9.slate400};
  transform: rotate(${props => (props.open ? 180 : 0)}deg);
  transition: transform 150ms;
  margin-left: 7px;
`;
const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 20px 16px 60px;
  background: ${T9.slate50};
  border-bottom: 1px solid ${T9.slate200};
`;
const DetailsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${T9.slate500};
`;
const ActionLink = styled(Link)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${T9.brand600};
  text-decoration: none;
  font-size: 13px;
  &:hover {
    text-decoration: underline;
  }
`;
const NoConnection = styled.div`
  font-size: 13px;
  color: ${T9.slate500};
`;

export const isMachineAllocated = (machine: MinionMachine) =>
  machine.allocation_status === "ALLOCATED" ||
  machine.allocation_status === "AVAILABLE" ||
  machine.allocation_status === "IN_USE";

type FilterType = "all" | "allocated" | "not-allocated";
type Props = {
  item?: MinionPool | null;
  transfers: TransferItem[];
  deployments: DeploymentItem[];
};
type State = {
  filterStatus: FilterType;
  openedRows: string[];
};
class MinionPoolMachines extends React.Component<Props, State> {
  state = {
    filterStatus: "all" as FilterType,
    openedRows: [] as string[],
  };

  get machines() {
    return this.props.item?.minion_machines || [];
  }

  get filteredMachines() {
    switch (this.state.filterStatus) {
      case "all":
        return this.machines;
      case "allocated":
        return this.machines.filter(isMachineAllocated);
      default:
        return this.machines.filter(m => !isMachineAllocated(m));
    }
  }

  handleRowClick(id: string) {
    if (this.state.openedRows.find(i => i === id)) {
      this.setState(prevState => ({
        openedRows: prevState.openedRows.filter(i => i !== id),
      }));
    } else {
      this.setState(prevState => ({
        openedRows: [...prevState.openedRows, id],
      }));
    }
  }

  findAction(machine: MinionMachine): ActionItem | null {
    if (!machine.allocated_action) {
      return null;
    }
    const find = (items: ActionItem[]) => items.find(i => i.id === machine.allocated_action);
    return find(this.props.transfers) || find(this.props.deployments) || null;
  }

  renderFilter() {
    const allocated = this.machines.filter(isMachineAllocated).length;
    return (
      <Segmented
        label="Allocation"
        value={this.state.filterStatus}
        onChange={value => {
          this.setState({ filterStatus: value as FilterType });
        }}
        options={[
          { label: "All", value: "all", count: this.machines.length },
          { label: "Allocated", value: "allocated", count: allocated },
          { label: "Not allocated", value: "not-allocated", count: this.machines.length - allocated },
        ]}
      />
    );
  }

  renderAllocatedAction(machine: MinionMachine) {
    if (!machine.allocated_action) {
      return <Muted>—</Muted>;
    }
    const action = this.findAction(machine);
    if (!action) {
      return <Mono title={machine.allocated_action}>{machine.allocated_action}</Mono>;
    }
    const isTransfer = action.type === "transfer";
    const title = getTransferItemTitle(action) || action.instances[0];
    return (
      <>
        <Pill tone={isTransfer ? "info" : "brand"}>{isTransfer ? "Transfer" : "Deployment"}</Pill>
        <ActionLink
          to={`/${isTransfer ? "transfers" : "deployments"}/${action.id}`}
          title={title}
          onClick={e => {
            e.stopPropagation();
          }}
        >
          {title}
        </ActionLink>
      </>
    );
  }

  renderConnectionInfo(machine: MinionMachine) {
    const info = machine.connection_info || {};
    const keys = Object.keys(info);
    return (
      <Details data-test-id="minionPoolMachines-details">
        <DetailsTitle>
          <Network size={14} />
          Connection info
        </DetailsTitle>
        {keys.length ? (
          <KeyValueGrid
            columns={3}
            items={keys.map(key => {
              const value = info[key];
              const text = value && typeof value === "object" ? JSON.stringify(value) : String(value ?? "");
              return {
                label: key,
                mono: key.toLowerCase().indexOf("password") === -1,
                value: key.toLowerCase().indexOf("password") > -1 ? <PasswordValue value={text} /> : text,
              };
            })}
          />
        ) : (
          <NoConnection>This agent machine has no connection info yet.</NoConnection>
        )}
        <KeyValueGrid
          columns={3}
          items={[
            { label: "Machine ID", value: machine.id, mono: true },
            { label: "Updated", value: machine.updated_at ? formatDateTime(machine.updated_at) : null },
            { label: "Last used", value: machine.last_used_at ? formatDateTime(machine.last_used_at) : null },
          ]}
        />
      </Details>
    );
  }

  renderMachines() {
    if (this.filteredMachines.length === 0) {
      return (
        <EmptyState
          compact
          icon={Monitor}
          tone="neutral"
          title="No agent machines found"
          message="No agent machine matches this filter."
          secondaryLabel="Show all"
          onSecondary={() => {
            this.setState({ filterStatus: "all" });
          }}
        />
      );
    }

    return (
      <Table>
        <TableInner>
          <ListHeader columns={MACHINE_COLUMNS} />
          {this.filteredMachines.map(machine => {
            const open = this.state.openedRows.includes(machine.id);
            return (
              <React.Fragment key={machine.id}>
                <ListRow
                  columns={MACHINE_COLUMNS}
                  onClick={() => {
                    this.handleRowClick(machine.id);
                  }}
                  trailing={<Chevron size={16} open={open} />}
                  testId="minionPoolMachines-row"
                >
                  <FlexCell gap={12} title={machine.id}>
                    <IconTile icon={Monitor} size={28} tone={isMachineAllocated(machine) ? "brand" : "neutral"} />
                    <Mono style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{machine.id}</Mono>
                  </FlexCell>
                  <div>
                    <StatePill status={machine.allocation_status} />
                  </div>
                  <div>
                    <StatePill status={machine.power_status} />
                  </div>
                  <FlexCell gap={8}>{this.renderAllocatedAction(machine)}</FlexCell>
                  <Cell title={machine.last_used_at ? formatDateTime(machine.last_used_at) : ""}>
                    {machine.last_used_at ? timeAgo(machine.last_used_at) : <Muted>Never</Muted>}
                  </Cell>
                  <Cell title={formatDateTime(machine.created_at)}>
                    {formatShortDateTime(machine.created_at)}
                  </Cell>
                </ListRow>
                {open ? this.renderConnectionInfo(machine) : null}
              </React.Fragment>
            );
          })}
        </TableInner>
      </Table>
    );
  }

  render() {
    const count = this.machines.length;
    const allocated = this.machines.filter(isMachineAllocated).length;
    if (!count) {
      return (
        <SectionCard title="Agent machines" icon={Monitor} testId="minionPoolMachines">
          <EmptyState
            compact
            icon={Monitor}
            tone="neutral"
            title="No agent machines"
            message="There are no agent machines allocated to this SwiftIQ Agent. Allocate the SwiftIQ Agent to create them."
          />
        </SectionCard>
      );
    }
    return (
      <SectionCard
        title="Agent machines"
        icon={Monitor}
        subtitle={
          <span data-test-id="minionPoolMachines-summary">
            {count} agent machine{count === 1 ? "" : "s"}, {allocated} allocated
          </span>
        }
        actions={this.renderFilter()}
        flush
        testId="minionPoolMachines"
      >
        {this.renderMachines()}
      </SectionCard>
    );
  }
}

export default MinionPoolMachines;
